// Derivaciones del hero de Cobranza: el bloqueo del equipo y lo que se sabe de la venta.
import type {
    LoanCreditSaleContext,
    LoanDeviceFleetItem,
    LoanInstallmentSaleContext,
} from '@/api/services/loan-credit';
import type { CreditFileBlock } from '../creditFile/api/useCreditFile';
import type { CreditTone } from '../creditFile/logic';
import { lockReadingOf, type LockReading } from '../lockPhase';
import { ACTIVE_UNCONFIRMED_LABEL, lockPhaseLabel, MDM_LOCK_PHASE_SHORT_LABEL } from '../mdm/logic';
import {
    SALE_CONTEXT_REASON_NOTE,
    UNREADABLE_CUSTOMER_NOTE,
    saleCustomerNote,
} from '../saleContext';

export interface DeviceLockChip {
    label: string;
    tone: CreditTone;
}

/** Cómo se pinta en el hero cada lectura del candado que no es el confirmado. */
const LOCK_READING_CHIP: Record<Exclude<LockReading, 'locked'>, DeviceLockChip> = {
    refuted: { label: MDM_LOCK_PHASE_SHORT_LABEL.refuted, tone: 'danger' },
    unconfirmed: { label: lockPhaseLabel('unconfirmed', undefined), tone: 'warning' },
    scheduled: { label: MDM_LOCK_PHASE_SHORT_LABEL.scheduled, tone: 'warning' },
    activeUnconfirmed: { label: ACTIVE_UNCONFIRMED_LABEL, tone: 'warning' },
    cutoffPassed: { label: lockPhaseLabel('cutoffPassed', undefined), tone: 'warning' },
    cutoffRefuted: { label: lockPhaseLabel('cutoffRefuted', undefined), tone: 'danger' },
};

/** `null` mientras el expediente carga; solo el candado confirmado dice «Bloqueado». */
export function deviceLockChipOf(
    device: CreditFileBlock<LoanDeviceFleetItem | null>,
    nowMs: number = Date.now(),
): DeviceLockChip | null {
    if (device.failure === 'forbidden') {
        return { label: 'Sin permiso para ver el equipo', tone: 'neutral' };
    }
    if (device.failure !== null) {
        return { label: 'Desconocido', tone: 'neutral' };
    }
    if (device.isLoading || device.data === undefined) return null;
    if (device.data === null) return { label: 'Sin dispositivo', tone: 'neutral' };
    const reading = lockReadingOf(device.data, new Date(nowMs));
    if (reading === 'locked') return { label: MDM_LOCK_PHASE_SHORT_LABEL.locked, tone: 'danger' };
    if (reading !== null) return LOCK_READING_CHIP[reading];
    return { label: 'Sin bloqueo', tone: 'success' };
}

const named = (value: string | null): value is string => value !== null && value.trim() !== '';

/** El nombre del cliente o cómo decir que falta; `note` explica el porqué al pasar el cursor. */
export type CustomerNameLabel =
    | { name: string }
    | { missing: 'No se pudo leer' | 'Sin nombre'; note: string };

const NAMELESS_CUSTOMER_NOTE =
    'El cliente de esta venta no tiene nombre registrado o no se pudo identificar.';

/** Regla única del nombre: la lectura caída es «No se pudo leer» y todo lo demás, «Sin nombre». */
export function customerNameLabelOf(
    sale: Pick<LoanInstallmentSaleContext, 'customerName' | 'customerNameStatus'> & {
        customerNameReason?: LoanInstallmentSaleContext['customerNameReason'];
    },
    note?: string,
): CustomerNameLabel {
    if (sale.customerNameStatus === 'OK' && named(sale.customerName)) {
        return { name: sale.customerName };
    }
    const reason =
        sale.customerNameReason === undefined
            ? ''
            : ` ${SALE_CONTEXT_REASON_NOTE[sale.customerNameReason]}`;
    if (sale.customerNameStatus === 'UNAVAILABLE') {
        return { missing: 'No se pudo leer', note: note ?? `${UNREADABLE_CUSTOMER_NOTE}${reason}` };
    }
    return { missing: 'Sin nombre', note: note ?? `${NAMELESS_CUSTOMER_NOTE}${reason}` };
}

/** El nombre del cliente del hero, con la nota detallada del contexto de venta del crédito. */
export function saleCustomerLabelOf(sale: LoanCreditSaleContext): CustomerNameLabel {
    return customerNameLabelOf(sale, saleCustomerNote(sale));
}

/** El nombre de la tienda sólo cuando el padrón lo resolvió. */
export function saleStoreLabelOf(sale: LoanCreditSaleContext): string | undefined {
    return sale.storeNameStatus === 'OK' && named(sale.storeName) ? sale.storeName : undefined;
}
